const API_BASE = 'https://api.driftcal.net';
const MAX_BYTES = 2 * 1024 * 1024;
const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

export const IMAGE_SLOTS = {
  PartnerLogo: {
    maxWidth: 800,
    maxHeight: 800,
    exact: false,
    accept: 'image/png,image/jpeg,image/webp',
    hint: 'PNG, JPEG or WebP. Max 800×800px, 2 MB.'
  },
  PartnerObjImage: {
    maxWidth: 900,
    maxHeight: 150,
    exact: true,
    accept: 'image/png,image/jpeg,image/webp',
    hint: 'PNG, JPEG or WebP. Exactly 900×150px, 2 MB.'
  },
  organiserImage: {
    maxWidth: 800,
    maxHeight: 800,
    exact: false,
    accept: 'image/png,image/jpeg,image/webp',
    hint: 'PNG, JPEG or WebP. Max 800×800px, 2 MB.'
  }
};

function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    img.src = url;
  });
}

export async function validateImageFile(file, type) {
  const slot = IMAGE_SLOTS[type];
  if (!slot) throw new Error('Unknown image type.');
  if (!file) throw new Error('Choose an image.');
  if (!ALLOWED_TYPES.has(file.type)) {
    throw new Error('Use PNG, JPEG or WebP.');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('Image must be 2 MB or smaller.');
  }
  const { width, height } = await readImageSize(file);
  if (slot.exact) {
    if (width !== slot.maxWidth || height !== slot.maxHeight) {
      throw new Error(
        `Must be exactly ${slot.maxWidth}×${slot.maxHeight}px (this file is ${width}×${height}).`
      );
    }
  } else if (width > slot.maxWidth || height > slot.maxHeight) {
    throw new Error(
      `Must be at most ${slot.maxWidth}×${slot.maxHeight}px (this file is ${width}×${height}).`
    );
  }
  return { width, height };
}

export async function uploadImageAsset({ type, entityId, file, sessionToken }) {
  await validateImageFile(file, type);
  if (!sessionToken) {
    throw new Error('Sign in again to upload images.');
  }
  if (!entityId) {
    throw new Error('Save the record before uploading.');
  }

  const headers = {
    'Content-Type': 'application/json',
    'x-session-token': sessionToken
  };
  const start = await fetch(`${API_BASE}/v1/asset-upload-url`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      type,
      entityId: Number(entityId),
      contentType: file.type,
      byteLength: file.size
    })
  });
  const startData = await start.json().catch(() => ({}));
  if (!start.ok) {
    throw new Error(startData.error || 'Could not start upload.');
  }

  const put = await fetch(startData.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file
  });
  if (!put.ok) {
    throw new Error('Upload to storage failed.');
  }

  const done = await fetch(`${API_BASE}/v1/asset-upload-complete`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      type,
      entityId: Number(entityId),
      key: startData.key
    })
  });
  const doneData = await done.json().catch(() => ({}));
  if (!done.ok) {
    throw new Error(doneData.error || 'Upload finished but cleanup failed.');
  }

  return startData.publicUrl;
}
